import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/testimonials';
import { faqItems } from '@/mocks/faq';
import { allArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const features = [
  {
    icon: 'ri-speed-up-line',
    title: '极速性能',
    description: '启动速度提升40%，内存占用降低30%，即使在老旧设备上也能流畅运行，不拖累系统性能。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '安全可靠',
    description: '通过多家安全厂商检测认证，不含病毒和恶意代码，无捆绑软件，保障您的数据和隐私安全。',
  },
  {
    icon: 'ri-stack-line',
    title: '功能全面',
    description: '涵盖系统工具、办公软件、多媒体处理、安全防护等200+实用功能，一个软件满足所有需求。',
  },
  {
    icon: 'ri-download-cloud-2-line',
    title: '完全免费',
    description: '承诺永久免费，所有核心功能不收取任何费用。无需注册、无广告打扰、无功能限制。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    description: '开发团队持续优化和添加新功能，每月稳定发布更新，确保您始终使用最新、最好的版本。',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '贴心服务',
    description: '提供完善的帮助文档、视频教程和技术支持，遇到问题24小时内响应，用心服务每一位用户。',
  },
];

const stats = [
  { value: '500万+', label: '累计用户', icon: 'ri-user-smile-line' },
  { value: '98.7%', label: '用户好评率', icon: 'ri-thumb-up-line' },
  { value: '200+', label: '实用功能', icon: 'ri-tools-line' },
  { value: '8年', label: '持续运营', icon: 'ri-time-line' },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const featuredArticles = allArticles.filter((a) => a.featured).slice(0, 3);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%2C%20minimalist%20digital%20art%2C%20amber%20and%20cream%20tones%2C%20modern%20tech%20aesthetic%2C%20subtle%20light%20particles%2C%20elegant%20and%20clean%20composition%2C%20professional%20software%20brand%20atmosphere&width=1800&height=900&seq=hero-91free&orientation=landscape"
          alt="91免费版 - 专业免费工具软件"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></div>
              <span className="text-background-50/90 text-sm">v4.2.0 最新版本已发布</span>
            </div>

            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-background-50 mb-6 leading-tight">
              专业工具 · 永久免费
              <br />
              <span className="text-primary-300">91免费版</span>
            </h1>
            <p className="text-background-50/80 text-lg md:text-xl mb-10 max-w-xl mx-auto leading-relaxed">
              覆盖系统工具、办公软件、多媒体处理等200+实用功能
              <br />
              无需付费，无需注册，让每个人都能享受专业级工具服务
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#download"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-lg text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-download-cloud-2-line text-lg"></i>
                免费下载 (Windows)
              </a>
              <a
                href="#download"
                className="px-8 py-3.5 bg-white/10 text-background-50 border border-white/30 rounded-lg text-base font-medium hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-apple-line text-lg"></i>
                macOS 版本下载
              </a>
            </div>

            <p className="text-background-50/50 text-xs mt-6">
              支持 Windows 10/11 · macOS 12+ · 安装包仅 85MB
            </p>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-background-50/60 text-2xl"></i>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative -mt-10 z-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 rounded-xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4 border border-background-200/70">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-50 flex items-center justify-center">
                  <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {stat.value}
                </div>
                <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
              核心优势
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              为什么选择91免费版？
            </h2>
            <p className="text-foreground-500 max-w-lg mx-auto text-sm">
              我们用八年时间打磨每一个功能，只为给你最好的免费工具体验
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-lg bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors duration-300">
                  <i className={`${feature.icon} text-primary-500 text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base">
                  {feature.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium mb-4">
              功能分类
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              探索全部功能分类
            </h2>
            <p className="text-foreground-500 max-w-lg mx-auto text-sm">
              六大功能分类，覆盖工作与生活的方方面面
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-100 transition-colors duration-300">
                    <i className={`${cat.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1 text-base">
                      {cat.name}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed mb-3 line-clamp-2">
                      {cat.description}
                    </p>
                    <span className="text-accent-600 text-xs font-medium">
                      {cat.articleCount} 篇教程
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-4">
                精选教程
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                热门文章推荐
              </h2>
            </div>
            <Link
              to="/category/system-tools"
              className="hidden sm:flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap transition-colors"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 rounded-md bg-primary-50 text-primary-600 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/system-tools"
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium whitespace-nowrap"
            >
              查看全部文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-background-100" id="testimonials">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-4">
              用户口碑
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              500万+用户的选择
            </h2>
            <p className="text-foreground-500 max-w-lg mx-auto text-sm">
              听听真实用户对91免费版的评价
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-6 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-secondary-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-heading font-semibold text-foreground-900 text-sm">
                      {t.name}
                    </div>
                    <div className="text-foreground-400 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-background-50" id="faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
              常见问题
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              你关心的都在这里
            </h2>
            <p className="text-foreground-500 text-sm">
              关于91免费版的常见疑问解答
            </p>
          </div>

          <div className="space-y-3">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="rounded-xl border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === faq.id ? null : faq.id)
                  }
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <span className="font-heading font-semibold text-foreground-900 text-sm pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-300 flex-shrink-0 ${
                      openFaq === faq.id ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === faq.id ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden" id="download">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20geometric%20shapes%2C%20minimalist%20digital%20art%2C%20amber%20and%20teal%20tones%2C%20modern%20tech%20atmosphere%2C%20clean%20elegant%20composition%2C%20subtle%20glow%20effects&width=1800&height=600&seq=cta-download-91free&orientation=landscape"
          alt="下载背景"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-950/80 to-secondary-950/90"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-4">
            准备好开始了吗？
          </h2>
          <p className="text-background-50/70 text-base mb-10 max-w-lg mx-auto">
            立即下载91免费版，解锁200+免费实用工具，让工作更高效、生活更便捷
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#download"
              className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-lg text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-windows-line text-lg"></i>
              Windows 版下载
            </a>
            <a
              href="#download"
              className="px-8 py-3.5 bg-white/10 text-background-50 border border-white/30 rounded-lg text-base font-medium hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-apple-line text-lg"></i>
              macOS 版下载
            </a>
          </div>

          <p className="text-background-50/40 text-xs mt-6">
            v4.2.0 · 发布于 2026年7月 · 安装包大小 85MB · 支持 Windows 10/11 和 macOS 12+
          </p>
        </div>
      </section>
    </main>
  );
}